<script setup lang="ts">
import type { BannedWordInstance } from "~/utils/banned-words";
import { getQuillTextRange } from "~/utils/banned-word-highlights";

const props = defineProps<{ quill: any; instances: BannedWordInstance[] }>();
const layer = ref<SVGSVGElement>();
const rects = ref<{ x: number; y: number; width: number; height: number }[]>([]);
// Draw the wave directly so SVG fragment URLs cannot be affected by a base URL.
const wavePath = (width: number) =>
  "M0 2" + " q1.5 -2 3 0 q1.5 2 3 0".repeat(Math.ceil(width / 6));
let scheduleUpdate = () => {};

watch(() => props.instances, () => scheduleUpdate(), { flush: "post" });

watch(() => props.quill, (quill, _previous, onCleanup) => {
  if (!quill?.root) return;
  const root = quill.root as HTMLElement;
  const view = root.ownerDocument.defaultView!;
  let frame = 0;
  let disposed = false;

  const update = () => {
    frame = 0;
    if (disposed) return;
    // Use the same drawing layer in every browser: API presence does not imply
    // that native custom-highlight backgrounds and decorations actually paint.
    const origin = layer.value?.getBoundingClientRect();
    if (!origin) return;
    rects.value = props.instances.flatMap((instance) =>
      [...instance.word.matchAll(/\S+/g)].flatMap((part) => {
        const range = getQuillTextRange(quill, instance.index + part.index!, part[0].length);
        return range ? Array.from(range.getClientRects())
          .filter((rect) => rect.width > 0 && rect.height > 0)
          .map((rect) => ({ x: rect.left - origin.left, y: rect.top - origin.top, width: rect.width, height: rect.height })) : [];
      })
    );
  };
  const schedule = () => {
    if (!disposed && !frame) frame = view.requestAnimationFrame(update);
  };
  scheduleUpdate = schedule;
  const onEditorChange = (event: string) => {
    if (event === "text-change") schedule(); // Includes silent imports/restores.
  };
  quill.on("editor-change", onEditorChange);

  // DOM nodes can be replaced by Quill while matching word offsets stay equal.
  const mutations = new MutationObserver(schedule);
  mutations.observe(root, { childList: true, characterData: true, subtree: true, attributes: true });

  const resize = new ResizeObserver(schedule);
  const page = layer.value?.parentElement;
  resize.observe(root);
  if (page) {
    resize.observe(page);
    // Header growth shifts the editor without changing its own size.
    for (const child of page.children) if (child !== layer.value) resize.observe(child);
    page.addEventListener("scroll", schedule, true);
  }
  view.addEventListener("resize", schedule);
  root.ownerDocument.fonts?.addEventListener("loadingdone", schedule);
  schedule();

  onCleanup(() => {
    disposed = true;
    view.cancelAnimationFrame(frame);
    quill.off("editor-change", onEditorChange);
    mutations.disconnect();
    resize?.disconnect();
    page?.removeEventListener("scroll", schedule, true);
    view.removeEventListener("resize", schedule);
    root.ownerDocument.fonts?.removeEventListener("loadingdone", schedule);
    rects.value = [];
    scheduleUpdate = () => {};
  });
}, { immediate: true, flush: "post" });
</script>

<template>
  <svg ref="layer" aria-hidden="true" data-banned-word-layer class="pointer-events-none absolute inset-0 z-10 h-full w-full overflow-visible select-none">
    <g v-for="(rect, index) in rects" :key="index" data-banned-word-marking>
      <rect :x="rect.x" :y="rect.y" :width="rect.width" :height="rect.height" class="fill-orange-300/30" />
      <svg :x="rect.x" :y="rect.y + rect.height + 3" :width="rect.width" height="4" class="overflow-hidden">
        <path :d="wavePath(rect.width)" class="fill-none stroke-[#e68a00] [stroke-width:1.2]" />
      </svg>
    </g>
  </svg>
</template>
